import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Container,
} from "@mui/material";
import { LogoImage } from "../components/layout/Logo";
import { get, post } from "../services/api";

interface InvitationDetails {
  invitationId: string;
  familyId: string;
  familyName: string;
  email: string;
  status: string;
  isExpired: boolean;
  createdAt: string;
  expiresAt: string;
}

const AcceptInvitation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    password: "",
    confirmPassword: "",
  });

  const invitationId = searchParams.get("id");

  useEffect(() => {
    if (!invitationId) {
      setError("Invalid invitation link");
      setLoading(false);
      return;
    }

    fetchInvitationDetails();
  }, [invitationId]);

  const fetchInvitationDetails = async () => {
    try {
      setLoading(true);
      const response = await get(`/api/invitations/${invitationId}`);

      setInvitation(response);
    } catch (err: any) {
      console.error("Error fetching invitation:", err);
      setError(err.message || "Failed to load invitation details");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange =
    (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!invitation) return;

    // Validation
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError("First name and last name are required");
      return;
    }

    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      await post("/api/invitations/accept", {
        invitationId: invitation.invitationId,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        password: formData.password,
      });

      setSuccess(true);

      // Redirect to login after success
      setTimeout(() => {
        navigate("/login", {
          state: {
            message:
              "Account created successfully! Please login with your credentials.",
            email: invitation.email,
          },
        });
      }, 2000);
    } catch (err: any) {
      console.error("Error accepting invitation:", err);
      setError(err.message || "Failed to accept invitation");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !invitation) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              width: "100%",
              textAlign: "center",
            }}
          >
            <LogoImage height={80} />
            <Typography
              variant="h4"
              gutterBottom
              sx={{ mt: 3, color: "error.main" }}
            >
              Invalid Invitation
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {error}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate("/register")}
              sx={{ mt: 2 }}
            >
              Create New Account
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              width: "100%",
              textAlign: "center",
            }}
          >
            <LogoImage height={80} />
            <Typography
              variant="h4"
              gutterBottom
              sx={{ mt: 3, color: "success.main" }}
            >
              Welcome to CanYa!
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Your account has been created successfully. You're being
              redirected to login...
            </Typography>
            <CircularProgress sx={{ mt: 2 }} />
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 3,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: "100%",
          }}
        >
          <Box textAlign="center" mb={3}>
            <LogoImage height={80} />
            <Typography variant="h4" gutterBottom sx={{ mt: 3 }}>
              Join {invitation?.familyName || "Family"}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Complete your account setup to join the family on CanYa
            </Typography>
          </Box>

          {invitation?.isExpired && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              This invitation has expired. Please ask for a new invitation.
            </Alert>
          )}

          {invitation && !invitation.isExpired && (
            <>
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.05)"
                      : "grey.50",
                  borderRadius: 1,
                  border: (theme) =>
                    theme.palette.mode === "dark"
                      ? "1px solid rgba(255,255,255,0.12)"
                      : "none",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.9)"
                        : "text.secondary",
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Invitation Details:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.8)"
                        : "text.primary",
                    mb: 0.5,
                  }}
                >
                  <strong style={{ color: "inherit" }}>Email:</strong>{" "}
                  {invitation.email}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.8)"
                        : "text.primary",
                  }}
                >
                  <strong style={{ color: "inherit" }}>Family:</strong>{" "}
                  {invitation.familyName}
                </Typography>
              </Box>

              <form onSubmit={handleSubmit}>
                <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                  <TextField
                    fullWidth
                    label="First Name"
                    variant="outlined"
                    value={formData.firstName}
                    onChange={handleInputChange("firstName")}
                    disabled={submitting}
                    required
                  />
                  <TextField
                    fullWidth
                    label="Last Name"
                    variant="outlined"
                    value={formData.lastName}
                    onChange={handleInputChange("lastName")}
                    disabled={submitting}
                    required
                  />
                </Box>

                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  variant="outlined"
                  value={formData.password}
                  onChange={handleInputChange("password")}
                  disabled={submitting}
                  required
                  helperText="Must be at least 8 characters long"
                  sx={{ mb: 3 }}
                />

                <TextField
                  fullWidth
                  label="Confirm Password"
                  type="password"
                  variant="outlined"
                  value={formData.confirmPassword}
                  onChange={handleInputChange("confirmPassword")}
                  disabled={submitting}
                  required
                  sx={{ mb: 3 }}
                />

                {error && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                  </Alert>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={submitting || invitation.isExpired}
                  sx={{ mb: 2 }}
                >
                  {submitting ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Creating Account...
                    </>
                  ) : (
                    "Join Family"
                  )}
                </Button>
              </form>
            </>
          )}

          <Box textAlign="center" mt={3}>
            <Typography
              variant="body2"
              sx={{
                color: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.7)"
                    : "text.secondary",
              }}
            >
              Already have an account?{" "}
              <Button
                variant="text"
                onClick={() => navigate("/login")}
                sx={{
                  textTransform: "none",
                  color: (theme) =>
                    theme.palette.mode === "dark" ? "#2BB3A3" : "primary.main",
                  fontWeight: 600,
                  "&:hover": {
                    color: (theme) =>
                      theme.palette.mode === "dark"
                        ? "#25a394"
                        : "primary.dark",
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(43,179,163,0.1)"
                        : "rgba(25,118,210,0.04)",
                  },
                }}
              >
                Sign In
              </Button>
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default AcceptInvitation;
